export const brazilianUfByCode: Record<string, string> = {
  "11": "RO", "12": "AC", "13": "AM", "14": "RR", "15": "PA", "16": "AP", "17": "TO",
  "21": "MA", "22": "PI", "23": "CE", "24": "RN", "25": "PB", "26": "PE", "27": "AL",
  "28": "SE", "29": "BA", "31": "MG", "32": "ES", "33": "RJ", "35": "SP", "41": "PR",
  "42": "SC", "43": "RS", "50": "MS", "51": "MT", "52": "GO", "53": "DF"
};

export type FiscalModel = "55" | "65" | "OUTRO";

export type NFeAccessKeyParts = {
  key: string;
  formatted: string;
  ufCode: string;
  uf: string;
  year: number;
  month: number;
  cnpj: string;
  modelCode: string;
  model: FiscalModel;
  series: string;
  number: string;
  emissionType: string;
  numericCode: string;
  checkDigit: number;
};

export type AccessKeyValidation =
  | { valid: true; parts: NFeAccessKeyParts; errors: [] }
  | { valid: false; key: string; formatted: string; errors: string[] };

export type NfceQrData = {
  valid: boolean;
  url?: string;
  hostname?: string;
  key?: string;
  version?: string;
  parameters: Record<string, string>;
  error?: string;
};

export function normalizeFiscalCode(value: string) {
  return String(value || "").replace(/\D/g, "").slice(0, 200);
}

export function formatAccessKey(value: string) {
  return normalizeFiscalCode(value).slice(0, 44).replace(/(\d{4})(?=\d)/g, "$1 ");
}

export function calculateAccessKeyCheckDigit(first43Digits: string) {
  if (!/^\d{43}$/.test(first43Digits)) return null;
  let weight = 2;
  let sum = 0;
  for (let index = first43Digits.length - 1; index >= 0; index -= 1) {
    sum += Number(first43Digits[index]) * weight;
    weight = weight === 9 ? 2 : weight + 1;
  }
  const candidate = 11 - (sum % 11);
  return candidate === 10 || candidate === 11 ? 0 : candidate;
}

export function parseNFeAccessKey(value: string): AccessKeyValidation {
  const key = normalizeFiscalCode(value);
  const formatted = formatAccessKey(key);
  const errors: string[] = [];
  if (key.length !== 44) {
    errors.push("A chave deve conter exatamente 44 dígitos.");
    return { valid: false, key, formatted, errors };
  }

  const ufCode = key.slice(0, 2);
  const yearMonth = key.slice(2, 6);
  const month = Number(yearMonth.slice(2));
  const cnpj = key.slice(6, 20);
  const modelCode = key.slice(20, 22);
  const expectedDigit = calculateAccessKeyCheckDigit(key.slice(0, 43));

  if (!brazilianUfByCode[ufCode]) errors.push("Código da UF inválido.");
  if (month < 1 || month > 12) errors.push("Mês de emissão inválido.");
  if (!["55", "65"].includes(modelCode)) errors.push("Modelo fiscal não suportado para NF-e/NFC-e.");
  if (expectedDigit === null || expectedDigit !== Number(key[43])) errors.push("Dígito verificador inválido.");
  if (errors.length) return { valid: false, key, formatted, errors };

  return {
    valid: true,
    errors: [],
    parts: {
      key,
      formatted,
      ufCode,
      uf: brazilianUfByCode[ufCode],
      year: 2000 + Number(yearMonth.slice(0, 2)),
      month,
      cnpj,
      modelCode,
      model: modelCode as FiscalModel,
      series: key.slice(22, 25),
      number: key.slice(25, 34),
      emissionType: key.slice(34, 35),
      numericCode: key.slice(35, 43),
      checkDigit: Number(key[43])
    }
  };
}

function officialNfceHost(hostname: string, configuredDomains: string[]) {
  const normalized = hostname.toLowerCase().replace(/\.$/, "");
  const configured = configuredDomains
    .map((value) => value.trim().toLowerCase().replace(/^https?:\/\//, "").split("/")[0].replace(/\.$/, ""))
    .filter(Boolean);
  if (configured.some((domain) => normalized === domain || normalized.endsWith(`.${domain}`))) return true;
  if (!normalized.endsWith(".gov.br")) return false;
  return normalized.split(".").some((label) => ["sefaz", "fazenda", "nfce", "sped", "set"].includes(label));
}

export function parseNfceQrCode(value: string, configuredDomains: string[] = []): NfceQrData {
  const raw = String(value || "").trim();
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { valid: false, parameters: {}, error: "O QR Code não contém uma URL válida." };
  }
  if (url.protocol !== "https:") return { valid: false, parameters: {}, error: "A consulta da NFC-e deve usar HTTPS." };
  if (url.username || url.password || url.port) return { valid: false, parameters: {}, error: "A URL do QR Code contém credenciais ou porta não permitida." };
  if (!officialNfceHost(url.hostname, configuredDomains)) return { valid: false, hostname: url.hostname, parameters: {}, error: "Domínio de consulta não reconhecido como oficial." };

  const parameters = Object.fromEntries(Array.from(url.searchParams.entries()).slice(0, 30));
  const payload = url.searchParams.get("p") || url.searchParams.get("chNFe") || url.searchParams.get("chave") || "";
  const pipeParts = payload.split("|");
  const candidate = (pipeParts[0]?.match(/\d{44}/)?.[0] || raw.match(/\d{44}/)?.[0] || "");
  const parsed = parseNFeAccessKey(candidate);
  if (!parsed.valid) {
    return { valid: false, url: url.toString(), hostname: url.hostname, parameters, error: "O QR Code oficial não contém uma chave de acesso válida." };
  }
  if (parsed.parts.model !== "65") {
    return { valid: false, url: url.toString(), hostname: url.hostname, key: parsed.parts.key, parameters, error: "A chave do QR Code não corresponde a uma NFC-e (modelo 65)." };
  }
  return {
    valid: true,
    url: url.toString(),
    hostname: url.hostname,
    key: parsed.parts.key,
    version: pipeParts[1] || url.searchParams.get("versao") || undefined,
    parameters
  };
}
